import { useTranslation } from "@opendash/i18n";
import dayjs, { Dayjs, locale } from "dayjs";

/**
 * Provides a function that returns a relative date label for a given date.
 * @returns Returns a function that provides a relative date label for a given date.
 */
export const useRelativeTime = () => {
  const t = useTranslation();

  function getRelativeDateLabel(date: Dayjs, hideTime?: boolean): string {
    if (!date) return "";

    const isToday = date.isSame(dayjs(), "day");
    const isAfter = date.isAfter(dayjs()) && !isToday;
    const isThisWeek = date.isSame(dayjs(), "week");
    const isNextWeek = date.isSame(dayjs().add(1, "week"), "week");

    const isWithinOneYear =
      dayjs().isBefore(date) &&
      dayjs().add(1, "year").add(-1, "day").isAfter(date);
    const format = "DD.MM." + (isWithinOneYear ? "" : "YYYY");

    const weekday = date
      .toDate()
      .toLocaleDateString(locale(), { weekday: "long" });

    const nextWeekday = t("maintenance:date.nextWeekday", { weekday });
    const thisWeekday = t("maintenance:date.thisWeekday", { weekday });
    const lastWeekday = t("maintenance:date.lastWeekday", { weekday });

    const today = t("maintenance:date.today");
    const time = hideTime ? "" : `, ${date.format("HH:mm")}`;
    const fullDate = date.format(format) + time;

    // when it is today
    if (isToday) return `${today}${time}`;

    // when it is within the next week but not in the past
    if (isAfter && isThisWeek) return `${thisWeekday}${time}`;
    if (!isAfter && isThisWeek) return `${lastWeekday}${time}`;
    if (isNextWeek) return `${nextWeekday}${time}`;

    // default is full date
    return fullDate;
  }

  return getRelativeDateLabel;
};
